import { Card, CardContent, Typography, CircularProgress, Box, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { Image } from '@/components/Analysis/shared/Image';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { StyledRemoveButton, CaptionBox, CaptionTextField, CaptionTypography } from './styles';
import { FileUploadStatus, FileTypes } from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { Attachment } from './type';

type AttachmentDisplayProps = {
  selectedAttachment: Attachment;
  selectedAttachmentIndex: number;
  showRemoveIcon: boolean;
  handleRemoveAttachment?: (index: number) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  imageCaption: string;
  setImageCaption: (caption: string) => void;
  handleImageCaption: () => void;
  handleCaption: (e: React.MouseEvent) => void;
  uploadProgress?: number;
  enableUpload?: boolean;
  cardHeight?: string;
  handleClick?: (e: React.MouseEvent) => void;
  isUploadDialog?: boolean;
  hasError?: boolean;
  handleError?: (success: boolean) => void;
};

export const AttachmentDisplay = ({
  selectedAttachment,
  selectedAttachmentIndex,
  showRemoveIcon,
  handleRemoveAttachment,
  isEditing,
  setIsEditing,
  imageCaption,
  setImageCaption,
  handleImageCaption,
  handleCaption,
  uploadProgress = 0,
  enableUpload = false,
  cardHeight = '80px',
  handleClick,
  isUploadDialog,
  hasError,
  handleError,
}: AttachmentDisplayProps) => {
  const renderFileContent = () => {
    const isImage =
      selectedAttachment.type.includes('image/') ||
      [FileTypes.Jpeg, FileTypes.Png].includes(selectedAttachment.type as FileTypes);
    const isPdf =
      selectedAttachment.type.includes('application/pdf') ||
      selectedAttachment.type === FileTypes.Pdf;
    const isVideo =
      selectedAttachment.type.includes('video/mp4') || selectedAttachment.type === FileTypes.Mp4;

    if (
      selectedAttachment.isNew &&
      enableUpload &&
      selectedAttachment.uploadStatus === FileUploadStatus.Inprogress
    ) {
      return (
        <CardContent
          sx={{
            display: 'flex',
            height: cardHeight,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: abyssColors.primary[100],
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              variant="determinate"
              value={uploadProgress}
              size={50}
              thickness={5}
              sx={{ color: abyssColors.secondary[500] }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" color="text.secondary">
                {`${Math.round(uploadProgress)}%`}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      );
    } else if (isImage) {
      return (
        <Image
          src={`${selectedAttachment.base64}`}
          alt={selectedAttachment.name}
          imgSx={{ height: cardHeight, objectFit: 'contain' }}
          sx={{
            height: cardHeight,
            cursor: hasError ? 'not-allowed' : 'pointer',
            pointerEvents: hasError ? 'all' : 'auto',
          }}
          onLoadEnd={handleError}
          fallbackSize="2.2rem"
          hideErrorMessage
        />
      );
    } else if (isPdf || isVideo) {
      return (
        <a href={selectedAttachment.url} target="_blank" rel="noopener noreferrer">
          <CardContent
            sx={{
              display: 'flex',
              height: cardHeight,
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: abyssColors.primary[100],
            }}
          >
            {isPdf && <AbyssIcon iconName="pdf-upload" pointer style={{ height: '4rem' }} />}
            {isVideo && (
              <SmartDisplayIcon sx={{ fontSize: 45, color: theme.palette.text.secondary }} />
            )}
          </CardContent>
        </a>
      );
    }
  };

  return (
    <>
      <Card sx={{ position: 'relative', overflow: 'visible' }} onClick={handleClick}>
        {renderFileContent()}
        {showRemoveIcon && handleRemoveAttachment && (
          <StyledRemoveButton
            aria-label="delete"
            onClick={() => handleRemoveAttachment(selectedAttachmentIndex)}
          >
            <CloseIcon style={{ fontSize: '13px' }} />
          </StyledRemoveButton>
        )}
      </Card>
      <CaptionBox onClick={handleCaption}>
        {!isUploadDialog && isEditing ? (
          <CaptionTextField
            value={imageCaption}
            size="small"
            onChange={(e) => setImageCaption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleImageCaption();
            }}
            onBlur={() => setIsEditing(false)}
            autoFocus
            multiline
            variant="outlined"
          />
        ) : (
          <Tooltip title={selectedAttachment.caption ?? selectedAttachment.name}>
            <CaptionTypography noWrap onClick={() => setIsEditing(true)}>
              {selectedAttachment.caption ?? selectedAttachment.name}
            </CaptionTypography>
          </Tooltip>
        )}
      </CaptionBox>
    </>
  );
};
